import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { useAIOutputReview, ReviewAction, OutputStatus } from '@/hooks/useAIOutputReview';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { CheckCircle2, XCircle, Edit3, ArrowUpCircle, HelpCircle, Sparkles, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

type Status = OutputStatus;
type Action = ReviewAction;

interface AIOutput {
  id: string;
  source_module: string;
  output_type: string;
  title: string;
  summary: string | null;
  payload: Record<string, unknown>;
  corrected_payload: Record<string, unknown> | null;
  confidence: number | null;
  status: Status;
  severity: string;
  generated_by: string;
  reviewer_id: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
  tenant_id: string | null;
}

const statusConfig: Record<Status, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  draft: { label: 'Draft', variant: 'secondary' },
  pending_review: { label: 'Pending Review', variant: 'default' },
  approved: { label: 'Approved', variant: 'default' },
  rejected: { label: 'Rejected', variant: 'destructive' },
  corrected: { label: 'Corrected', variant: 'default' },
  escalated: { label: 'Escalated', variant: 'destructive' },
  more_evidence_requested: { label: 'More Evidence Requested', variant: 'outline' },
};

const HITLReviewWorkbench = () => {
  const { tenantId } = useAuth();
  const { role } = useUserRole();
  const { submitting, submitAction: submitReview } = useAIOutputReview();
  const [outputs, setOutputs] = useState<AIOutput[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<AIOutput | null>(null);
  const [action, setAction] = useState<Action | null>(null);
  const [notes, setNotes] = useState('');
  const [correctedJson, setCorrectedJson] = useState('');
  const [tab, setTab] = useState<Status | 'all'>('pending_review');

  const canReview = ['program_manager', 'pmo', 'executive', 'admin'].includes(role || '');

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('ai_outputs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100);
    if (error) toast.error(error.message);
    else setOutputs((data as AIOutput[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const channel = supabase
      .channel('ai-outputs')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ai_outputs' }, load)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const seedDemo = async () => {
    const { error } = await supabase.from('ai_outputs').insert([
      {
        source_module: 'signal_engine',
        output_type: 'risk_signal',
        title: 'Schedule slip detected on Project Riyadh Metro',
        summary: 'Velocity dropped 32% over last 2 sprints. Likely milestone delay.',
        payload: { project: 'Riyadh Metro', metric: 'velocity', delta: -0.32 },
        confidence: 0.84,
        severity: 'high',
        tenant_id: tenantId,
      },
      {
        source_module: 'risk_prediction',
        output_type: 'recommendation',
        title: 'Reallocate 2 engineers to Vision 2030 PMO project',
        summary: 'Resource gap identified vs. milestone burn-down.',
        payload: { recommendation: 'reallocate_resources', count: 2 },
        confidence: 0.72,
        severity: 'medium',
        tenant_id: tenantId,
      },
      {
        source_module: 'weekly_digest',
        output_type: 'report',
        title: 'Executive Weekly Digest - Week 18',
        summary: '12 projects on-track, 3 at risk, 1 critical.',
        payload: { on_track: 12, at_risk: 3, critical: 1 },
        confidence: 0.91,
        severity: 'low',
        tenant_id: tenantId,
      },
    ]);
    if (error) toast.error(error.message);
    else toast.success('Seeded 3 demo AI outputs');
  };

  const openAction = (o: AIOutput, a: Action) => {
    setSelected(o);
    setAction(a);
    setNotes('');
    setCorrectedJson(JSON.stringify(o.payload, null, 2));
  };

  const handleSubmit = async () => {
    if (!selected || !action) return;

    let corrected: Record<string, unknown> | null = null;
    if (action === 'correct') {
      try { corrected = JSON.parse(correctedJson); }
      catch { toast.error('Corrected payload must be valid JSON'); return; }
    }

    const result = await submitReview({
      outputId: selected.id,
      currentStatus: selected.status,
      currentTenantId: selected.tenant_id,
      action,
      notes,
      correctedPayload: corrected,
    });

    if (result.ok === false) { toast.error(result.error); return; }

    toast.success(`Output updated`);
    setSelected(null);
    setAction(null);
  };

  const filtered = tab === 'all' ? outputs : outputs.filter((o) => o.status === tab);

  const counts = {
    pending_review: outputs.filter((o) => o.status === 'pending_review').length,
    approved: outputs.filter((o) => o.status === 'approved').length,
    rejected: outputs.filter((o) => o.status === 'rejected').length,
    corrected: outputs.filter((o) => o.status === 'corrected').length,
    escalated: outputs.filter((o) => o.status === 'escalated').length,
    more_evidence_requested: outputs.filter((o) => o.status === 'more_evidence_requested').length,
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              HITL Review Workbench
            </CardTitle>
            <CardDescription>
              Review · Correct · Approve AI-generated outputs before they reach end users
            </CardDescription>
          </div>
          {import.meta.env.DEV && (role === 'admin' || role === 'pmo') && (
            <Button variant="outline" size="sm" onClick={seedDemo}>
              Seed demo outputs
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={tab} onValueChange={(v) => setTab(v as Status | 'all')}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="pending_review">Pending ({counts.pending_review})</TabsTrigger>
            <TabsTrigger value="approved">Approved ({counts.approved})</TabsTrigger>
            <TabsTrigger value="corrected">Corrected ({counts.corrected})</TabsTrigger>
            <TabsTrigger value="rejected">Rejected ({counts.rejected})</TabsTrigger>
            <TabsTrigger value="escalated">Escalated ({counts.escalated})</TabsTrigger>
            <TabsTrigger value="more_evidence_requested">More Evidence ({counts.more_evidence_requested})</TabsTrigger>
            <TabsTrigger value="all">All</TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Sparkles className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No AI outputs in this state.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((o) => (
                  <div key={o.id} className="border border-border rounded-lg p-4 hover:bg-muted/40 transition-colors">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Badge variant={statusConfig[o.status].variant}>
                            {statusConfig[o.status].label}
                          </Badge>
                          <Badge variant="outline" className="text-xs">{o.source_module}</Badge>
                          {o.confidence != null && (
                            <Badge variant="outline" className="text-xs">
                              {Math.round(o.confidence * 100)}% confidence
                            </Badge>
                          )}
                          {o.severity === 'high' && <Badge variant="destructive" className="text-xs">High severity</Badge>}
                        </div>
                        <h4 className="font-medium text-foreground">{o.title}</h4>
                        {o.summary && <p className="text-sm text-muted-foreground mt-1">{o.summary}</p>}
                        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(o.created_at).toLocaleString()}
                          {o.reviewed_at && ` · reviewed ${new Date(o.reviewed_at).toLocaleString()}`}
                        </p>
                      </div>
                    </div>

                    {o.review_notes && (
                      <div className="text-xs bg-muted/50 rounded px-2 py-1 mb-2">
                        <span className="font-medium">Reviewer note:</span> {o.review_notes}
                      </div>
                    )}

                    {canReview && o.status === 'pending_review' && (
                      <div className="flex gap-2 flex-wrap pt-2 border-t border-border">
                        <Button size="sm" variant="default" onClick={() => openAction(o, 'approve')}>
                          <CheckCircle2 className="w-4 h-4 me-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openAction(o, 'correct')}>
                          <Edit3 className="w-4 h-4 me-1" /> Correct
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openAction(o, 'escalate')}>
                          <ArrowUpCircle className="w-4 h-4 me-1" /> Escalate
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openAction(o, 'request_evidence')}>
                          <HelpCircle className="w-4 h-4 me-1" /> Request More Evidence
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => openAction(o, 'reject')}>
                          <XCircle className="w-4 h-4 me-1" /> Reject
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      <Dialog open={!!action} onOpenChange={(o) => { if (!o) { setAction(null); setSelected(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {action === 'approve' && 'Approve AI Output'}
              {action === 'reject' && 'Reject AI Output'}
              {action === 'correct' && 'Correct AI Output'}
              {action === 'escalate' && 'Escalate AI Output'}
              {action === 'request_evidence' && 'Request More Evidence'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm">
              <p className="font-medium">{selected?.title}</p>
              <p className="text-muted-foreground">{selected?.summary}</p>
            </div>
            {action === 'correct' && (
              <div>
                <label className="text-xs font-medium text-muted-foreground">Corrected payload (JSON)</label>
                <Textarea
                  value={correctedJson}
                  onChange={(e) => setCorrectedJson(e.target.value)}
                  rows={8}
                  className="font-mono text-xs"
                />
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground">Notes</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={['reject', 'escalate', 'request_evidence'].includes(action ?? '') ? 'Reason (required)' : 'Optional notes'}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAction(null); setSelected(null); }}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || (['reject', 'escalate', 'request_evidence'].includes(action ?? '') && !notes.trim())}
              variant={action === 'reject' ? 'destructive' : 'default'}
            >
              {submitting && <Loader2 className="w-4 h-4 me-2 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default HITLReviewWorkbench;
